import { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import SelectableListItem from 'components/SelectableListItem';
import SortableListItem from 'components/SortableListItem';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

import { useDialog } from 'components/Dialog';
import Alert from '@mui/material/Alert';

type Props<T> = {
  items: readonly T[];
  title?: ReactNode;
  initial?: string[];
  loading?: boolean;
  noItemsText?: ReactNode;
  noItemsAction?: ReactNode;
  renderItem: (item: T) => ReactNode;
  filter?: (item: T, search: string) => boolean;
  onClose?: () => void;
  onSave?: (values: string[]) => void;
};

type Item = {
  readonly id: string;
};

function ColumnEditor<T extends Item>(props: Props<T>) {
  const {
    items,
    title,
    initial = [],
    loading,
    noItemsText = 'Nėra pasirinkimų',
    noItemsAction,
    renderItem,
    filter = () => true,
    onClose,
    onSave,
  } = props;

  const { close } = useDialog();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(initial);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  const handleSelectionChange = useCallback(
    (id: string) => (selected: boolean) => {
      setSelected(current =>
        selected ? [...current, id] : current.filter(item => item !== id)
      );
    },
    []
  );

  const handleRemoveItem = useCallback(
    (id: string) => () => {
      setSelected(current => current.filter(item => item !== id));
    },
    []
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = selected.indexOf(active.id as string);
        const newIndex = selected.indexOf(over.id as string);

        setSelected(arrayMove(selected, oldIndex, newIndex));
      }
    },
    [selected]
  );

  const handleSaveClick = useCallback(() => {
    onSave && onSave(selected);
  }, [onSave, selected]);

  const handleCloseClick = useCallback(() => {
    close();
    onClose && onClose();
  }, [close, onClose]);

  const hasItems = items.length > 0;

  return (
    <>
      {title && <DialogTitle>{title}</DialogTitle>}

      <DialogContent dividers sx={{ maxHeight: 'calc(100vh - 40vh)' }}>
        {hasItems ? (
          <Box sx={{ display: 'flex', height: '100%' }}>
            <Box sx={{ width: '50%' }}>
              <TextField
                fullWidth
                variant="standard"
                placeholder="ieškoti..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{ endAdornment: <SearchIcon /> }}
                sx={{ mb: 2 }}
              />

              <List
                sx={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  height: 'calc(100% - 48px)',
                }}>
                {items
                  .filter(item => filter(item, search.toLowerCase()))
                  .map(item => (
                    <SelectableListItem
                      key={item.id}
                      selected={selected.includes(item.id)}
                      onChange={handleSelectionChange(item.id)}>
                      {renderItem(item)}
                    </SelectableListItem>
                  ))}
              </List>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ ml: 2, mr: 2 }} />

            <Box sx={{ width: '50%' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext
                  strategy={verticalListSortingStrategy}
                  items={selected}>
                  <List
                    sx={{
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      height: '100%',
                    }}>
                    {selected.map(id => {
                      const selectedItem = items.find(item => item.id === id);

                      return (
                        selectedItem && (
                          <SortableListItem
                            key={selectedItem.id}
                            id={selectedItem.id}
                            secondaryAction={
                              <ConfirmableIconButton
                                size="small"
                                onConfirm={handleRemoveItem(selectedItem.id)}>
                                <DeleteIcon fontSize="small" />
                              </ConfirmableIconButton>
                            }>
                            {renderItem(selectedItem)}
                          </SortableListItem>
                        )
                      );
                    })}
                  </List>
                </SortableContext>
              </DndContext>
            </Box>
          </Box>
        ) : (
          <Alert severity="error" action={noItemsAction}>
            {noItemsText}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseClick} variant="text">
          Uždaryti
        </Button>

        <LoadingButton
          startIcon={<SaveIcon />}
          disabled={!hasItems}
          variant="contained"
          loading={loading}
          onClick={handleSaveClick}>
          Saugoti
        </LoadingButton>
      </DialogActions>
    </>
  );
}

export default ColumnEditor;
